import { useState, useEffect } from "react";
import { DollarSign, TrendingUp, TrendingDown, Shield, Activity, PieChart, Clock } from "lucide-react";
import { API_BASE } from "@/lib/api";

interface Position {
  market_id: string;
  side: string;
  entry_price: number;
  shares: number;
  value: number;
  strategy: string;
}

interface Stats {
  bankroll: number;
  total_pnl: number;
  daily_pnl: number;
  win_rate: number;
  max_drawdown: number;
  exposure: number;
}

export default function PortfolioPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [balance, setBalance] = useState<any>(null);

  const fetchData = async () => {
    try {
      const [statsR, posR, balR] = await Promise.all([
        fetch(`${API_BASE}/api/portfolio/stats`),
        fetch(`${API_BASE}/api/portfolio/positions`),
        fetch(`${API_BASE}/api/portfolio/balance`),
      ]);
      setStats(await statsR.json());
      const posData = await posR.json();
      setPositions(posData.positions || []);
      setBalance(await balR.json());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const totalValue = positions.reduce((sum, p) => sum + (p.value || 0), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <PieChart size={18} className="text-terminal-accent" />
            PORTFOLIO
          </h1>
          <p className="text-xs text-gray-500 mt-1">Real-time positions, P&L, and risk metrics</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">LAST UPDATE</div>
          <div className="text-xs text-terminal-accent">{new Date().toLocaleTimeString()}</div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-6 gap-4">
        <StatCard label="BANKROLL" value={stats ? `$${stats.bankroll?.toLocaleString()}` : "--"} icon={DollarSign} color="text-green-400" />
        <StatCard label="TOTAL P&L" value={stats ? `${(stats.total_pnl ?? 0) >= 0 ? "+" : ""}$${(stats.total_pnl ?? 0).toFixed(2)}` : "--"} change={stats ? `${((stats.total_pnl ?? 0) / (stats.bankroll || 1) * 100).toFixed(2)}%` : undefined} icon={(stats?.total_pnl ?? 0) >= 0 ? TrendingUp : TrendingDown} color={(stats?.total_pnl ?? 0) >= 0 ? "text-green-400" : "text-red-400"} />
        <StatCard label="DAILY P&L" value={stats ? `${stats.daily_pnl >= 0 ? "+" : ""}$${stats.daily_pnl?.toFixed(2)}` : "--"} icon={Activity} color="text-blue-400" />
        <StatCard label="WIN RATE" value={stats ? `${(stats.win_rate * 100).toFixed(1)}%` : "--"} icon={Shield} color="text-yellow-400" />
        <StatCard label="MAX DD" value={stats ? `${(stats.max_drawdown * 100).toFixed(1)}%` : "--"} icon={TrendingDown} color="text-red-400" />
        <StatCard label="EXPOSURE" value={stats ? `${(stats.exposure * 100).toFixed(1)}%` : "--"} icon={PieChart} color="text-purple-400" />
      </div>

      {/* Positions Table */}
      <div className="terminal-window">
        <div className="terminal-header">
          <span className="flex items-center gap-2"><Clock size={12} /> OPEN POSITIONS</span>
          <span className="text-terminal-accent">{positions.length} positions | ${totalValue.toFixed(2)} total</span>
        </div>
        <div className="p-3">
          {positions.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-8">No open positions</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-terminal-border text-gray-500 text-xs">
                  <th className="text-left px-2 py-2">MARKET</th>
                  <th className="text-left px-2 py-2">SIDE</th>
                  <th className="text-right px-2 py-2">ENTRY</th>
                  <th className="text-right px-2 py-2">SHARES</th>
                  <th className="text-right px-2 py-2">VALUE</th>
                  <th className="text-right px-2 py-2">STRATEGY</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p, i) => (
                  <tr key={i} className="border-b border-terminal-border/50 hover:bg-white/5">
                    <td className="px-2 py-2 text-white">{p.market_id}</td>
                    <td className={`px-2 py-2 ${p.side === "YES" ? "text-green-400" : "text-red-400"}`}>{p.side}</td>
                    <td className="px-2 py-2 text-right">{p.entry_price.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right">{p.shares.toFixed(0)}</td>
                    <td className="px-2 py-2 text-right text-white">${p.value.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right text-terminal-accent text-xs">{p.strategy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Allocation */}
      {positions.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          <div className="terminal-window p-4">
            <div className="text-xs text-gray-500 mb-3">ALLOCATION BY STRATEGY</div>
            <div className="space-y-2">
              {Object.entries(
                positions.reduce((acc, p) => {
                  acc[p.strategy] = (acc[p.strategy] || 0) + p.value;
                  return acc;
                }, {} as Record<string, number>)
              ).map(([strat, val]) => (
                <div key={strat} className="flex items-center gap-2">
                  <div className="text-xs text-white w-32">{strat}</div>
                  <div className="flex-1 bg-terminal-border rounded h-2 overflow-hidden">
                    <div
                      className="bg-terminal-accent h-full"
                      style={{ width: `${(val / totalValue) * 100}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-400 w-16 text-right">${val.toFixed(0)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="terminal-window p-4">
            <div className="text-xs text-gray-500 mb-3">ALLOCATION BY SIDE</div>
            <div className="space-y-2">
              {["YES", "NO"].map((side) => {
                const val = positions.filter((p) => p.side === side).reduce((s, p) => s + p.value, 0);
                return (
                  <div key={side} className="flex items-center gap-2">
                    <div className={`text-xs w-32 ${side === "YES" ? "text-green-400" : "text-red-400"}`}>{side}</div>
                    <div className="flex-1 bg-terminal-border rounded h-2 overflow-hidden">
                      <div
                        className={side === "YES" ? "bg-green-500 h-full" : "bg-red-500 h-full"}
                        style={{ width: `${(val / totalValue) * 100}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-400 w-16 text-right">${val.toFixed(0)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, change, icon: Icon, color }: any) {
  return (
    <div className="terminal-window p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">{label}</span>
        <Icon size={14} className={color} />
      </div>
      <div className="text-lg font-bold text-white">{value}</div>
      {change && <div className="text-xs mt-1 text-terminal-accent">{change}</div>}
    </div>
  );
}
